// Authentication API service with multi-tenant support

import { apiClient } from './apiClient';
import { tenantService } from './tenantService';

export interface RegisterData {
  email: string;
  company: string;
  password: string;
  tenant_code?: string; // Optional: auto-generated from company name if not provided
}

export interface LoginData {
  username: string;
  password: string;
  tenant_code?: string;
}

export interface User {
  id: string;
  username: string;
  email: string;
  // `name` is derived server-side (first_name + last_name, falling
  // back to username) so the UI always has a non-empty header.
  name: string;
  first_name: string;
  last_name: string;
  phone: string;
  location: string;
  department: string;
  bio: string;
  // ISO timestamps from Django; the FE formats for display.
  date_joined?: string;
  last_login?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
  // Tenant context — populated by /auth/me/ and the JWT login response.
  tenants?: string[];
  default_tenant?: string;
  tenant_code?: string;
  /** Coarse status labels in the active tenant: e.g. "admin", "member",
   *  "superuser". Use `assigned_roles` for the actual RBAC role names. */
  roles?: string[];
  /** True when the user is a tenant administrator (TenantUser.is_admin
   *  or Django superuser) in the active tenant. Unambiguous flag for
   *  UI gating — distinct from the generic `is_admin` wildcard below. */
  is_tenant_admin?: boolean;
  /** RBAC role assignments in the active tenant, with human-readable
   *  names. Empty list = user has no assigned roles in this tenant. */
  assigned_roles?: { code: string; name: string; description?: string }[];
  // Effective RBAC permissions in the active tenant. `is_admin: true`
  // means wildcard grant (Django superuser or TenantUser.is_admin) — the
  // FE should treat that as "every check passes" and not bother enumerating.
  is_admin?: boolean;
  permissions?: string[];
  // Legacy fields kept so existing call-sites don't break; new code
  // should prefer the explicit shape above.
  company?: string;
  created_at?: string;
}

/**
 * Fields the FE Profile page is allowed to send to PATCH /api/auth/me/.
 * Mirrors backend `MeUpdateSerializer` — keep the two in sync. Username,
 * email, password, and the staff flags are intentionally not editable
 * here; each has its own (current or future) flow.
 */
export interface UpdateMeData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  location?: string;
  department?: string;
  bio?: string;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: User;
  tenant_code?: string;
  message?: string;
}

export const authApi = {
  /**
   * Register a new user and create tenant
   * After successful registration, redirects to tenant subdomain
   */
  register: async (data: RegisterData): Promise<AuthResponse> => {
    // Generate derived fields
    const cleanCompany = data.company.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const tenantCode = data.tenant_code || cleanCompany.substring(0, 20);
    const username = data.email.split('@')[0];

    // Payload matches backend expectations
    const payload = {
      tenant_name: data.company,
      tenant_code: tenantCode,
      db_name: tenantCode,
      admin_username: username,
      admin_email: data.email,
      admin_password: data.password,
      company_name: data.company,
      username: username,
      email: data.email,
      password: data.password
    };

    // PublicSignupView returns a 201 body of shape
    //   { tenant: {...}, admin_user: {...}, tokens: { access, refresh }, message }
    // — there's no top-level `success` or `token` field, so the older
    // `if (response.success)` check never fired and the FE silently
    // appeared to do nothing on successful signup. We now treat
    // "the POST didn't throw" as success (apiClient.post throws on
    // non-2xx) and normalise the response shape ourselves.
    const raw = await apiClient.post<any>(
      '/public/signup/',
      payload,
      { skipTenant: true }
    );

    const token = raw?.tokens?.access || raw?.token;
    const finalTenantCode = raw?.tenant?.code || raw?.tenant_code || tenantCode;

    if (token) {
      localStorage.setItem('auth_token', token);
      if (raw.admin_user) {
        localStorage.setItem('user', JSON.stringify(raw.admin_user));
      }
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        tenantService.setTenantCode(finalTenantCode);
      }
    }

    return {
      success: true,
      token,
      tenant_code: finalTenantCode,
      user: raw?.admin_user,
      message: raw?.message,
    };
  },

  /**
   * Login user within tenant context
   */
  login: async (data: LoginData): Promise<AuthResponse> => {
    // Note: Updated to handle standard JWT response which might not have 'success' field
    const payload = {
      username: data.username,
      password: data.password,
    };

    // Cast to any to handle raw response differences
    // Standard JWT response is { access: string, refresh: string }
    const rawResponse = await apiClient.post<any>('/auth/token/', payload, { skipTenant: true });

    // Check for standard JWT 'access' token or custom 'token' field
    const token = rawResponse.token || rawResponse.access;
    const refreshToken = rawResponse.refresh;

    // Construct normalized response
    const response: AuthResponse = {
      success: !!token,
      token: token,
      user: rawResponse.user,
      message: rawResponse.detail || rawResponse.message
    };

    if (response.success && response.token) {
      localStorage.setItem('auth_token', response.token);
      // Refresh token is now set as an HttpOnly cookie by the BE — we
      // explicitly drop any value present in the body (defence against
      // the BE accidentally also returning it). Anything below this line
      // assumes refresh is unreachable from JS.
      localStorage.removeItem('refresh_token');

      // BE returns default_tenant (and tenants[]); persist it so future requests
      // can send X-Tenant-Code header. user.tenant_code is the legacy fallback.
      const tenantCode =
        rawResponse.default_tenant ||
        (Array.isArray(rawResponse.tenants) ? rawResponse.tenants[0] : undefined) ||
        response.user?.tenant_code;
      if (tenantCode) tenantService.setTenantCode(tenantCode);

      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }
    }

    return response;
  },

  /**
   * Verify the email-confirmation token from the welcome email.
   * Public endpoint; no tenant context needed.
   */
  verifyEmail: async (token: string): Promise<{ verified: boolean; email: string; username: string; verified_at: string | null }> => {
    return apiClient.post('/public/verify-email/', { token }, { skipTenant: true });
  },

  /**
   * Request a password-reset email. The BE always returns 200 with
   * the same generic message — there's no FE branch on success vs
   * "no such email" because the BE deliberately doesn't tell us.
   */
  forgotPassword: async (email: string): Promise<{ detail: string }> => {
    return apiClient.post('/public/forgot-password/', { email }, { skipTenant: true });
  },

  /**
   * Consume a reset token + set the new password. Doesn't log the
   * user in — caller should redirect to /login.
   */
  resetPassword: async (token: string, password: string): Promise<{ reset: boolean; email: string; username: string }> => {
    return apiClient.post('/public/reset-password/', { token, password }, { skipTenant: true });
  },

  /**
   * Logout user and clear tenant context
   */
  logout: async (): Promise<void> => {
    try {
      // BE clears the HttpOnly refresh cookie + best-effort blacklists
      // the refresh token. We always run the local cleanup below even if
      // the network call fails so the FE never gets stuck "logged in".
      await apiClient.post('/auth/logout/', {}, { skipTenant: true });
    } catch {
      /* fall through to local cleanup */
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      tenantService.clearTenant();
    }
  },

  /**
   * Get current user profile
   */
  getProfile: async (): Promise<User> => {
    return apiClient.get<User>('/auth/profile', { skipTenant: true });
  },

  /**
   * Get current user details (Me).
   *
   * Sends the active tenant code so the BE can compute tenant-scoped
   * roles (admin vs member). `skipTenant: true` would suppress that and
   * give a less useful payload to the Profile page.
   */
  getMe: async (): Promise<User> => {
    return apiClient.get<User>('/auth/me/');
  },

  /**
   * Update editable profile fields (PATCH /api/auth/me/).
   *
   * Returns the same envelope shape as getMe so callers can drop the
   * result straight into a React Query cache without a second fetch.
   */
  updateMe: async (data: UpdateMeData): Promise<User> => {
    return apiClient.patch<User>('/auth/me/', data);
  },

  /**
   * Refresh auth token. The refresh JWT now lives in an HttpOnly cookie
   * so we send no body — the browser attaches the cookie automatically.
   */
  refreshToken: async (): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/refresh/', {}, { skipTenant: true });
    if (response.success && response.token) {
      localStorage.setItem('auth_token', response.token);
    }
    return response;
  },

  /**
   * Validate tenant code availability
   */
  validateTenantCode: async (tenantCode: string): Promise<{ available: boolean }> => {
    return apiClient.get<{ available: boolean }>(
      `/auth/validate-tenant/${tenantCode}`,
      { skipTenant: true }
    );
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('auth_token') && tenantService.isOnTenantDomain();
  },

  /**
   * Get stored user
   */
  getStoredUser: (): User | null => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
};
